import { memo, useMemo, useRef } from 'react';
import BarChartVertical from '@/components/BarChartVertical';
import { useTheme } from '@simplywallst/ui-core';

export interface Props {
  companyName: string;
  industryName: string;
  marketName: string;
  revenueGrowthAnnual: number | null;
  earningsGrowthAnnual: number | null;
  revenueGrowthAnnualMarket: number;
  netIncomeGrowthAnnualMarket: number;
  revenueGrowthAnnualIndustry: number;
  netIncomeGrowthAnnualIndustry: number;
  earningsGrowthAnnualFormatted: string;
  netIncomeGrowthAnnualIndustryFormatted: string;
  netIncomeGrowthAnnualMarketFormatted: string;
  revenueGrowthAnnualFormatted: string;
  revenueGrowthAnnualIndustryFormatted: string;
  revenueGrowthAnnualMarketFormatted: string;
  labels?: {
    company: string;
    industry: string;
    market: string;
    chartEarnings: string;
    chartRevenue: string;
  };
}

const FutureGrowth = ({
  companyName,
  industryName,
  marketName,
  revenueGrowthAnnual,
  earningsGrowthAnnual,
  revenueGrowthAnnualMarket,
  netIncomeGrowthAnnualMarket,
  revenueGrowthAnnualIndustry,
  netIncomeGrowthAnnualIndustry,
  earningsGrowthAnnualFormatted,
  netIncomeGrowthAnnualIndustryFormatted,
  netIncomeGrowthAnnualMarketFormatted,
  revenueGrowthAnnualFormatted,
  revenueGrowthAnnualIndustryFormatted,
  revenueGrowthAnnualMarketFormatted,
  labels = {
    company: 'Company',
    industry: 'Industry',
    market: 'Market',
    chartEarnings: 'Forecast annual earnings growth',
    chartRevenue: 'Forecast annual revenue growth',
  },
}: Props) => {
  const theme = useTheme();

  const chartsRef = useRef([
    'forecast annual earnings growth',
    'forecast annual revenue growth',
  ] as const);

  const { current: charts } = chartsRef;

  const chartLabels = useMemo(
    () => [labels.chartEarnings, labels.chartRevenue],
    [labels.chartEarnings, labels.chartRevenue]
  );

  const keysRef = useRef(['Company', 'Industry', 'Market'] as const);

  const { current: keys } = keysRef;

  const keyLabels = useMemo(
    () => [labels.company, labels.industry, labels.market],
    [labels.company, labels.industry, labels.market]
  );

  const entities = {
    [charts[0]]: {
      [keys[0]]: earningsGrowthAnnual,
      [keys[1]]: netIncomeGrowthAnnualIndustry,
      [keys[2]]: netIncomeGrowthAnnualMarket,
    },
    [charts[1]]: {
      [keys[0]]: revenueGrowthAnnual,
      [keys[1]]: revenueGrowthAnnualIndustry,
      [keys[2]]: revenueGrowthAnnualMarket,
    },
  };

  const entitiesFormatted = {
    [charts[0]]: {
      [keys[0]]: earningsGrowthAnnualFormatted,
      [keys[1]]: netIncomeGrowthAnnualIndustryFormatted,
      [keys[2]]: netIncomeGrowthAnnualMarketFormatted,
    },
    [charts[1]]: {
      [keys[0]]: revenueGrowthAnnualFormatted,
      [keys[1]]: revenueGrowthAnnualIndustryFormatted,
      [keys[2]]: revenueGrowthAnnualMarketFormatted,
    },
  };

  const entityTooltips = {
    [charts[0]]: {
      [keys[0]]: companyName,
      [keys[1]]: industryName,
      [keys[2]]: marketName,
    },
    [charts[1]]: {
      [keys[0]]: companyName,
      [keys[1]]: industryName,
      [keys[2]]: marketName,
    },
  };

  return (
    <BarChartVertical
      charts={charts as [string, string]}
      chartLabels={chartLabels}
      entities={entities}
      entitiesFormatted={entitiesFormatted}
      entityTooltips={entityTooltips}
      keys={keys as [string, string, string]}
      keyLabels={keyLabels}
      colors={theme.chart.barChart.futureGrowth.colors}
      titleColors={theme.chart.barChart.futureGrowth.titleColors}
      height={250}
    />
  );
};

export default memo(FutureGrowth);
